import { useMemo, useState, Fragment } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { CreatorGuide } from './Guide'

type Account = { name: string | null; fb_user_id: string | null; status: string | null; tema: string | null; gender: string | null; login?: string | null; password?: string | null; cookies?: string | null; created_at: string | null }
type Page = { profile_id: string | null; page_id: string | null; name: string | null; url: string | null; tema: string | null; account_name: string | null; groups: string[]; created_at: string | null }
type CreatedGroup = { group_id: string | null; name: string | null; url: string | null; page_profile_id: string | null; page_name: string | null; account_name: string | null; tema: string | null; members?: number | null; members_24h?: number | null; members_7d?: number | null; login?: string | null; password?: string | null; cookies?: string | null; created_at: string | null }
type MediaPool = { tema: string; kind: string; level: string; remaining?: number | null; total?: number | null }
type Snapshot = { accounts: Account[]; pages: Page[]; createdGroups?: CreatedGroup[]; media_pool?: MediaPool[]; counts: { accounts: number; pages: number; groups: number; createdGroups?: number }; generatedAt: string }
type Resp = { ok: boolean; data: Snapshot | null; updatedAt: string | null; creatorVersion?: string | null }
type ActivityLog = { id: number; type: 'group' | 'story' | 'invite'; page_name: string | null; page_profile_id: string | null; target_name: string | null; target_url: string | null; post_url: string | null; link: string | null; count: number | null; status: string | null; error_message: string | null; posted_at: number | null }
type ActivityResp = { date: string; counts: { group: number; story: number; invite: number }; logs: ActivityLog[] }
type ReportPage = { page_name: string | null; page_profile_id: string | null; groups: number; stories: number; invites: number; last_at: number | null }
type GroupInviteRow = { group_name: string; group_url: string | null; invites: number; pages: number }
type ReportResp = { date: string; totals: { group_posts: number; stories: number; invite_rows: number; invites: number; group_invites: number; pages_active: number }; pages: ReportPage[]; group_invites_by_group?: GroupInviteRow[] }
type GrowthStatus = 'atingido' | 'no_ritmo' | 'atrasado' | 'parado' | 'sem_dados'
type GrowthPage = { page_id: string; name: string | null; followers: number; rate_per_day: number | null; measured_days: number | null; age_days: number | null; eta_days: number | null; status: GrowthStatus }
type GrowthResumo = { total: number; atingido: number; no_ritmo: number; atrasado: number; parado: number; sem_dados: number; taxa_mediana: number; ritmo_necessario: number }
type GrowthResp = { ok: boolean; pages: GrowthPage[]; resumo: GrowthResumo | null; meta: { target: number; days: number; window: number }; scoped?: boolean }

function copy(text: string | null | undefined) {
  if (!text) return
  navigator.clipboard?.writeText(text).catch(() => {})
}

function AccessCell({ login, password, cookies }: { login?: string | null; password?: string | null; cookies?: string | null }) {
  const btn = 'rounded bg-muted/40 px-1.5 py-0.5 text-[10px] hover:bg-muted/70'
  return (
    <div className="space-y-0.5">
      {login ? (
        <button className="text-primary hover:underline text-[11px] block max-w-[180px] truncate" title={`Copiar login: ${login}`} onClick={() => copy(login)}>{login}</button>
      ) : <span className="text-muted-foreground">—</span>}
      <div className="flex gap-1">
        {password ? <button className={btn} title="Copiar senha" onClick={() => copy(password)}>senha</button> : null}
        {cookies ? <button className={btn} title="Copiar cookies (JSON)" onClick={() => copy(cookies)}>cookies</button> : null}
      </div>
    </div>
  )
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'
  const s = Math.floor((Date.now() - t) / 1000)
  if (s < 60) return `há ${s}s`
  if (s < 3600) return `há ${Math.floor(s / 60)}min`
  if (s < 86400) return `há ${Math.floor(s / 3600)}h`
  return `há ${Math.floor(s / 86400)}d`
}

const PERIODS: Record<string, number> = { hoje: 1, '7d': 7, '30d': 30 }
function periodCutoff(period: string): number | null {
  if (period === 'todos' || !PERIODS[period]) return null
  if (period === 'hoje') { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime() }
  return Date.now() - PERIODS[period] * 86400_000
}
function inPeriod(iso: string | null, cutoff: number | null): boolean {
  if (cutoff == null) return true
  if (!iso) return false
  const t = new Date(iso).getTime()
  return !Number.isNaN(t) && t >= cutoff
}
function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—'
}
function todayStr(): string {
  const d = new Date(); const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
function fmtTime(ms: number | null): string {
  return ms ? new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'
}
const ACT_META: Record<string, { label: string; cls: string }> = {
  group: { label: 'Grupo', cls: 'bg-sky-500/15 text-sky-400' },
  story: { label: 'Story', cls: 'bg-fuchsia-500/15 text-fuchsia-400' },
  invite: { label: 'Convite', cls: 'bg-emerald-500/15 text-emerald-400' },
}

function StatusPill({ status }: { status: string | null }) {
  const s = (status || '').toLowerCase()
  let cls = 'bg-muted/40 text-muted-foreground'
  if (s === 'ativa' || s === 'created') cls = 'bg-emerald-500/15 text-emerald-400'
  else if (s.includes('sms')) cls = 'bg-sky-500/15 text-sky-400'
  else if (s.includes('verif')) cls = 'bg-amber-500/15 text-amber-400'
  else if (s.includes('restrit') || s.includes('bloque')) cls = 'bg-red-500/15 text-red-400'
  return <span className={`rounded px-1.5 py-0.5 text-[10px] ${cls}`}>{status || '—'}</span>
}

function GroupDelta({ value }: { value?: number | null }) {
  if (value == null) return <span className="text-muted-foreground">—</span>
  const c = value > 0 ? '#22c55e' : value < 0 ? '#ef4444' : undefined
  return <span style={{ color: c }}>{value > 0 ? '+' : ''}{value.toLocaleString('pt-BR')}</span>
}

function GrowthPill({ status }: { status: GrowthStatus }) {
  const map: Record<GrowthStatus, [string, string]> = {
    atingido: ['bg-emerald-500/15 text-emerald-400', 'atingiu a meta'],
    no_ritmo: ['bg-sky-500/15 text-sky-400', 'no ritmo'],
    atrasado: ['bg-amber-500/15 text-amber-400', 'atrasada'],
    parado: ['bg-red-500/15 text-red-400', 'parada'],
    sem_dados: ['bg-muted/40 text-muted-foreground', 'sem dados'],
  }
  const [cls, label] = map[status] || map.sem_dados
  return <span className={`rounded px-1.5 py-0.5 text-[10px] ${cls}`}>{label}</span>
}

export function Creator() {
  const { data, isLoading, error, refetch, isFetching } = useQuery<Resp>({
    queryKey: ['creator-snapshot'],
    queryFn: () => api.get('/automacao-fb/creator/snapshot'),
    refetchInterval: 30000,
  })
  const snap = data?.data

  const [q, setQ] = useState('')
  const [flag, setFlag] = useState('')
  const [status, setStatus] = useState('')
  const [period, setPeriod] = useState('todos')
  const [tab, setTab] = useState<'contas' | 'paginas' | 'grupos' | 'atividade' | 'relatorio' | 'esteira' | 'catalogo' | 'guia'>('contas')
  const [gSort, setGSort] = useState<'members' | 'members_24h' | 'members_7d'>('members_7d')
  const [logDate, setLogDate] = useState(todayStr())
  const [logType, setLogType] = useState('')

  const { data: actData, isFetching: actFetching, refetch: actRefetch } = useQuery<ActivityResp>({
    queryKey: ['creator-activity', logDate, logType],
    queryFn: () => api.get(`/automacao-fb/creator/activity-logs?date=${logDate}${logType ? `&type=${logType}` : ''}`),
    enabled: tab === 'atividade',
    refetchInterval: tab === 'atividade' ? 20000 : false,
  })
  const actCounts = actData?.counts
  const actTotal = actCounts ? actCounts.group + actCounts.story + actCounts.invite : 0

  const [expandedPage, setExpandedPage] = useState<string | null>(null)

  const { data: repData, isFetching: repFetching, refetch: repRefetch } = useQuery<ReportResp>({
    queryKey: ['creator-report', logDate],
    queryFn: () => api.get(`/automacao-fb/creator/activity-report?date=${logDate}`),
    enabled: tab === 'relatorio',
    refetchInterval: tab === 'relatorio' ? 15000 : false,
  })

  // posts em grupo do dia (pra drill-down por página no relatório)
  const { data: repGroups } = useQuery<ActivityResp>({
    queryKey: ['creator-report-groups', logDate],
    queryFn: () => api.get(`/automacao-fb/creator/activity-logs?type=group&date=${logDate}`),
    enabled: tab === 'relatorio',
    refetchInterval: tab === 'relatorio' ? 30000 : false,
  })
  // CATÁLOGO GLOBAL de doc_id (auto-correção do creator) — só ler, pra debug de rotação
  const { data: catalogData, isFetching: catalogFetching, refetch: catalogRefetch } = useQuery<{ ok: boolean; count: number; entries: { friendly: string; doc_id: string; last_seen: string | null }[] }>({
    queryKey: ['creator-doc-ids'],
    queryFn: () => api.get('/automacao-fb/creator/doc-ids'),
    enabled: tab === 'catalogo',
  })
  const [catQ, setCatQ] = useState('')

  // ESTEIRA DE DADOS — crescimento real de cada página e projeção até a meta (1k em 5d)
  const { data: growthData, isFetching: growthFetching, refetch: growthRefetch } = useQuery<GrowthResp>({
    queryKey: ['creator-growth'],
    queryFn: () => api.get('/automacao-fb/creator/growth-estimate'),
    enabled: tab === 'esteira',
    refetchInterval: tab === 'esteira' ? 60000 : false,
  })

  const groupsByPage = useMemo(() => {
    const m: Record<string, ActivityLog[]> = {}
    for (const l of repGroups?.logs || []) {
      const k = l.page_profile_id || l.page_name || '?'
      if (!m[k]) m[k] = []
      m[k].push(l)
    }
    return m
  }, [repGroups])

  const flags = useMemo(() => {
    const set = new Set<string>()
    snap?.accounts.forEach(a => a.tema && set.add(a.tema))
    snap?.pages.forEach(p => p.tema && set.add(p.tema))
    return Array.from(set).sort()
  }, [snap])
  const statuses = useMemo(() => {
    const set = new Set<string>()
    snap?.accounts.forEach(a => a.status && set.add(a.status))
    return Array.from(set).sort()
  }, [snap])

  const cutoff = periodCutoff(period)
  const needle = q.trim().toLowerCase()

  const accounts = useMemo(() => (snap?.accounts || []).filter(a =>
    (!flag || a.tema === flag) &&
    (!status || a.status === status) &&
    inPeriod(a.created_at, cutoff) &&
    (!needle || [a.name, a.login, a.fb_user_id].some(v => (v || '').toLowerCase().includes(needle)))
  ), [snap, flag, status, cutoff, needle])

  const pages = useMemo(() => (snap?.pages || []).filter(p =>
    (!flag || p.tema === flag) &&
    inPeriod(p.created_at, cutoff) &&
    (!needle || [p.name, p.account_name].some(v => (v || '').toLowerCase().includes(needle)))
  ), [snap, flag, cutoff, needle])

  const groups = useMemo(() => (snap?.createdGroups || []).filter(g =>
    (!flag || g.tema === flag) &&
    inPeriod(g.created_at, cutoff) &&
    (!needle || [g.name, g.page_name, g.account_name].some(v => (v || '').toLowerCase().includes(needle)))
  // ranking: quem mais cresceu em 7d primeiro (nulos por último), como a aba Seguidores
  // ranking: ordena pela coluna escolhida (24h / 7d / membros), maior primeiro, nulos por último
  ).sort((a, b) => ((b[gSort] as number) ?? Number.NEGATIVE_INFINITY) - ((a[gSort] as number) ?? Number.NEGATIVE_INFINITY)), [snap, flag, cutoff, needle, gSort])

  // Resumos (sobre o conjunto FILTRADO)
  const byStatus = useMemo(() => {
    const m = new Map<string, number>()
    accounts.forEach(a => { const k = a.status || '—'; m.set(k, (m.get(k) || 0) + 1) })
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1])
  }, [accounts])
  const pagesByFlag = useMemo(() => {
    const m = new Map<string, number>()
    pages.forEach(p => { const k = p.tema || '—'; m.set(k, (m.get(k) || 0) + 1) })
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1])
  }, [pages])
  const accountsWithPage = useMemo(() => {
    const withPage = new Set(pages.map(p => p.account_name).filter(Boolean) as string[])
    return accounts.filter(a => a.name && withPage.has(a.name)).length
  }, [accounts, pages])

  const selCls = 'rounded-md border border-border bg-card px-2 py-1 text-xs'

  return (
    <div className="p-6 md:p-8 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Creator</h1>
          <p className="text-sm text-muted-foreground">Resumo detalhado dos perfis, páginas e grupos criados no app Creator.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <button onClick={() => setTab(tab === 'guia' ? 'contas' : 'guia')} className={`rounded-md border px-2 py-1 ${tab === 'guia' ? 'border-primary text-primary' : 'border-border hover:bg-muted/40'}`}>
            Guia
          </button>
          <span>Última sincronização: {timeAgo(data?.updatedAt ?? null)}</span>
          {data?.creatorVersion && <span className="rounded bg-muted/40 px-2 py-0.5">v{data.creatorVersion}</span>}
          <button onClick={() => refetch()} className="rounded-md border border-border px-2 py-1 hover:bg-muted/40">
            {isFetching ? 'Atualizando…' : 'Atualizar'}
          </button>
        </div>
      </div>

      {snap?.media_pool && snap.media_pool.length > 0 && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          <span className="font-semibold">Mídia acabando/esgotada</span> — repetir derruba o engajamento, reponha as pastas:
          <span className="ml-1">
            {snap.media_pool.map((m, i) => (
              <span key={i} className="ml-2 mt-1 inline-block rounded bg-red-500/15 px-2 py-0.5">
                {m.tema} · {m.kind} · {m.level}{m.remaining != null && m.total != null ? ` (${m.remaining}/${m.total})` : ''}
              </span>
            ))}
          </span>
        </div>
      )}

      {tab === 'guia' ? (
        <CreatorGuide />
      ) : isLoading ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : error ? (
        <div className="text-sm text-red-400">Erro ao carregar o snapshot.</div>
      ) : !snap ? (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-500">
          Nenhum dado ainda. Abra o Creator no desktop, faça login do painel em Configurações → “Painel Blissf” e ative a sincronização.
        </div>
      ) : (
        <>
          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-2">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar nome, login, FBID…" className={`${selCls} w-56`} />
            <select value={flag} onChange={e => setFlag(e.target.value)} className={selCls}>
              <option value="">Todas as personas</option>
              {flags.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select value={status} onChange={e => setStatus(e.target.value)} className={selCls}>
              <option value="">Todos os status</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={period} onChange={e => setPeriod(e.target.value)} className={selCls}>
              <option value="todos">Qualquer data</option>
              <option value="hoje">Hoje</option>
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
            </select>
            {(q || flag || status || period !== 'todos') && (
              <button onClick={() => { setQ(''); setFlag(''); setStatus(''); setPeriod('todos') }} className="text-xs text-muted-foreground hover:text-foreground underline">limpar</button>
            )}
          </div>

          {/* Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-2xl font-bold">{accounts.length}</div>
              <div className="text-xs text-muted-foreground">Perfis {(flag || status || period !== 'todos' || q) ? '(filtro)' : ''}</div>
              <div className="mt-1 text-[10px] text-muted-foreground">{accountsWithPage} com página</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-2xl font-bold">{pages.length}</div>
              <div className="text-xs text-muted-foreground">Páginas</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-2xl font-bold">{groups.length}</div>
              <div className="text-xs text-muted-foreground">Grupos criados</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground mb-1">Perfis por status</div>
              <div className="flex flex-wrap gap-1">
                {byStatus.length ? byStatus.map(([s, n]) => (
                  <span key={s} className="inline-flex items-center gap-1"><StatusPill status={s} /><span className="text-[10px] text-muted-foreground">{n}</span></span>
                )) : <span className="text-[10px] text-muted-foreground">—</span>}
              </div>
            </div>
          </div>

          {pagesByFlag.length > 1 && (
            <div className="rounded-xl border border-border bg-card p-3">
              <div className="text-xs text-muted-foreground mb-2">Páginas por persona</div>
              <div className="flex flex-wrap gap-2">
                {pagesByFlag.map(([f, n]) => (
                  <button key={f} onClick={() => setFlag(f === '—' ? '' : f)} className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-muted/40">
                    <span className="font-semibold">{f}</span> <span className="text-muted-foreground">{n}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Abas */}
          <div className="flex gap-1 border-b border-border">
            {([['contas', `Perfis (${accounts.length})`], ['paginas', `Páginas (${pages.length})`], ['grupos', `Grupos (${groups.length})`], ['atividade', `Postagens${actData ? ` (${actTotal})` : ''}`], ['relatorio', 'Relatório'], ['esteira', 'Estimativas'], ['catalogo', 'Catálogo']] as const).map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === id ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{label}</button>
            ))}
          </div>

          {tab === 'contas' && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="max-h-[520px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
                    <th className="px-4 py-2">Nome</th><th className="px-4 py-2">FB ID</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Persona</th><th className="px-4 py-2">Página?</th><th className="px-4 py-2">Acesso</th><th className="px-4 py-2">Criada</th>
                  </tr></thead>
                  <tbody>
                    {accounts.map((a, i) => {
                      const has = !!a.name && pages.some(p => p.account_name === a.name)
                      return (
                        <tr key={i} className="border-t border-border/40 hover:bg-muted/20 align-top">
                          <td className="px-4 py-1.5">{a.name || '—'}</td>
                          <td className="px-4 py-1.5 text-muted-foreground">{a.fb_user_id || '—'}</td>
                          <td className="px-4 py-1.5"><StatusPill status={a.status} /></td>
                          <td className="px-4 py-1.5">{a.tema || '—'}</td>
                          <td className="px-4 py-1.5">{has ? <span className="text-emerald-400">sim</span> : <span className="text-muted-foreground">não</span>}</td>
                          <td className="px-4 py-1.5"><AccessCell login={a.login} password={a.password} cookies={a.cookies} /></td>
                          <td className="px-4 py-1.5 text-muted-foreground">{fmtDate(a.created_at)}</td>
                        </tr>
                      )
                    })}
                    {accounts.length === 0 && <tr><td colSpan={7} className="px-4 py-3 text-muted-foreground">Nenhum perfil com esse filtro.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'paginas' && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="max-h-[520px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
                    <th className="px-4 py-2">Página</th><th className="px-4 py-2">Persona</th><th className="px-4 py-2">Grupos</th><th className="px-4 py-2">Criada</th><th className="px-4 py-2">Link</th>
                  </tr></thead>
                  <tbody>
                    {pages.map((p, i) => (
                      <tr key={i} className="border-t border-border/40 hover:bg-muted/20 align-top">
                        <td className="px-4 py-1.5">{p.name || '—'}<div className="text-[10px] text-muted-foreground">{p.account_name || ''}</div></td>
                        <td className="px-4 py-1.5">{p.tema || '—'}</td>
                        <td className="px-4 py-1.5">
                          {p.groups && p.groups.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {p.groups.map((g, gi) => (
                                <a key={gi} href={`https://www.facebook.com/groups/${g}`} target="_blank" rel="noreferrer" className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary hover:bg-primary/20">{g}</a>
                              ))}
                            </div>
                          ) : <span className="text-muted-foreground">0</span>}
                        </td>
                        <td className="px-4 py-1.5 text-muted-foreground">{fmtDate(p.created_at)}</td>
                        <td className="px-4 py-1.5">{p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">abrir</a> : '—'}</td>
                      </tr>
                    ))}
                    {pages.length === 0 && <tr><td colSpan={5} className="px-4 py-3 text-muted-foreground">Nenhuma página com esse filtro.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'grupos' && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="max-h-[520px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
                    <th className="px-4 py-2">Grupo</th>
                    {([['members', 'Membros'], ['members_24h', '24h'], ['members_7d', '7 dias']] as const).map(([k, label]) => (
                      <th key={k} className="px-4 py-2 text-right"><button onClick={() => setGSort(k)} className={`inline-flex items-center gap-0.5 hover:text-foreground ${gSort === k ? 'text-foreground font-semibold' : ''}`}>{label}{gSort === k ? ' ▾' : ''}</button></th>
                    ))}
                    <th className="px-4 py-2">Perfil que gerencia</th><th className="px-4 py-2">Conta</th><th className="px-4 py-2">Acesso</th><th className="px-4 py-2">Persona</th><th className="px-4 py-2">Criado</th><th className="px-4 py-2">Link</th>
                  </tr></thead>
                  <tbody>
                    {groups.map((g, i) => (
                      <tr key={i} className="border-t border-border/40 hover:bg-muted/20 align-top">
                        <td className="px-4 py-1.5">{g.name || g.group_id || '—'}<div className="text-[10px] text-muted-foreground">{g.group_id || ''}</div></td>
                        <td className="px-4 py-1.5 text-right font-semibold">{g.members != null ? g.members.toLocaleString('pt-BR') : '—'}</td>
                        <td className="px-4 py-1.5 text-right"><GroupDelta value={g.members_24h} /></td>
                        <td className="px-4 py-1.5 text-right"><GroupDelta value={g.members_7d} /></td>
                        <td className="px-4 py-1.5">{g.page_name || '—'}<div className="text-[10px] text-muted-foreground">{g.page_profile_id || ''}</div></td>
                        <td className="px-4 py-1.5">{g.account_name || '—'}</td>
                        <td className="px-4 py-1.5"><AccessCell login={g.login} password={g.password} cookies={g.cookies} /></td>
                        <td className="px-4 py-1.5">{g.tema || '—'}</td>
                        <td className="px-4 py-1.5 text-muted-foreground">{fmtDate(g.created_at)}</td>
                        <td className="px-4 py-1.5">{(g.url || g.group_id) ? <a href={g.url || `https://www.facebook.com/groups/${g.group_id}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">abrir</a> : '—'}</td>
                      </tr>
                    ))}
                    {groups.length === 0 && <tr><td colSpan={10} className="px-4 py-3 text-muted-foreground">Nenhum grupo com esse filtro.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'atividade' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-muted-foreground">Dia</label>
                <input type="date" value={logDate} max={todayStr()} onChange={e => setLogDate(e.target.value)} className={selCls} />
                <button onClick={() => setLogDate(todayStr())} className="text-xs text-muted-foreground hover:text-foreground underline">hoje</button>
                <select value={logType} onChange={e => setLogType(e.target.value)} className={selCls}>
                  <option value="">Todos os tipos</option>
                  <option value="group">Grupos</option>
                  <option value="story">Stories</option>
                  <option value="invite">Convites</option>
                </select>
                {actCounts && (
                  <div className="flex items-center gap-1.5 text-[11px]">
                    <span className={`rounded px-1.5 py-0.5 ${ACT_META.group.cls}`}>Grupos {actCounts.group}</span>
                    <span className={`rounded px-1.5 py-0.5 ${ACT_META.story.cls}`}>Stories {actCounts.story}</span>
                    <span className={`rounded px-1.5 py-0.5 ${ACT_META.invite.cls}`}>Convites {actCounts.invite}</span>
                  </div>
                )}
                <button onClick={() => actRefetch()} className="ml-auto rounded-md border border-border px-2 py-1 text-xs hover:bg-muted/40">
                  {actFetching ? 'Atualizando…' : 'Atualizar'}
                </button>
              </div>

              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="max-h-[520px] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
                      <th className="px-4 py-2">Tipo</th><th className="px-4 py-2">Página</th><th className="px-4 py-2">Alvo / Destino</th><th className="px-4 py-2">Link</th><th className="px-4 py-2">Horário</th><th className="px-4 py-2">Status</th>
                    </tr></thead>
                    <tbody>
                      {(actData?.logs || []).map(l => {
                        const meta = ACT_META[l.type] || { label: l.type, cls: 'bg-muted/40 text-muted-foreground' }
                        const alvo = l.type === 'group'
                          ? (l.target_name || (l.target_url ? 'grupo' : '—'))
                          : l.type === 'story'
                            ? (l.link || '—')
                            : `${l.count ?? 0} convites`
                        const alvoHref = l.type === 'group' ? l.target_url : (l.type === 'story' ? l.link : null)
                        return (
                          <tr key={l.id} className="border-t border-border/40 hover:bg-muted/20 align-top">
                            <td className="px-4 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[10px] ${meta.cls}`}>{meta.label}</span></td>
                            <td className="px-4 py-1.5">{l.page_name || '—'}</td>
                            <td className="px-4 py-1.5 max-w-[240px] truncate" title={alvo}>
                              {alvoHref ? <a href={alvoHref} target="_blank" rel="noreferrer" className="text-primary hover:underline">{alvo}</a> : alvo}
                            </td>
                            <td className="px-4 py-1.5">{l.post_url ? <a href={l.post_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">abrir</a> : '—'}</td>
                            <td className="px-4 py-1.5 text-muted-foreground whitespace-nowrap">{fmtTime(l.posted_at)}</td>
                            <td className="px-4 py-1.5"><StatusPill status={l.status} />{l.error_message ? <div className="text-[10px] text-red-400 max-w-[180px] truncate" title={l.error_message}>{l.error_message}</div> : null}</td>
                          </tr>
                        )
                      })}
                      {(!actData || actData.logs.length === 0) && <tr><td colSpan={6} className="px-4 py-3 text-muted-foreground">Nenhuma postagem nesse dia.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === 'relatorio' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-muted-foreground">Dia</label>
                <input type="date" value={logDate} max={todayStr()} onChange={e => setLogDate(e.target.value)} className={selCls} />
                <button onClick={() => setLogDate(todayStr())} className="text-xs text-muted-foreground hover:text-foreground underline">hoje</button>
                <span className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> ao vivo (15s)
                  <button onClick={() => repRefetch()} className="ml-1 rounded-md border border-border px-2 py-0.5 hover:bg-muted/40">{repFetching ? '…' : 'atualizar'}</button>
                </span>
              </div>

              {/* KPIs do dia */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {([
                  ['Posts em grupo', repData?.totals.group_posts ?? 0, '#38bdf8'],
                  ['Stories', repData?.totals.stories ?? 0, '#e879f9'],
                  ['Convites p/ seguir', repData?.totals.invites ?? 0, '#22c55e'],
                  ['Convites p/ grupos', repData?.totals.group_invites ?? 0, '#c084fc'],
                  ['Rodadas de convite', repData?.totals.invite_rows ?? 0, '#a3a3a3'],
                  ['Páginas ativas', repData?.totals.pages_active ?? 0, '#f59e0b'],
                ] as const).map(([label, val, color]) => (
                  <div key={label} className="rounded-xl border border-border bg-card p-4">
                    <div className="text-2xl font-bold" style={{ color }}>{val.toLocaleString('pt-BR')}</div>
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>

              {/* Tabela por página */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="max-h-[520px] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
                      <th className="px-4 py-2">Página</th><th className="px-4 py-2 text-right">Grupos</th><th className="px-4 py-2 text-right">Stories</th><th className="px-4 py-2 text-right">Convites</th><th className="px-4 py-2">Última ação</th>
                    </tr></thead>
                    <tbody>
                      {(repData?.pages || []).map(p => {
                        const key = p.page_profile_id || p.page_name || '?'
                        const gposts = groupsByPage[key] || []
                        const open = expandedPage === key
                        return (
                          <Fragment key={key}>
                            <tr className={`border-t border-border/40 hover:bg-muted/20 ${p.groups ? 'cursor-pointer' : ''}`} onClick={() => p.groups && setExpandedPage(open ? null : key)}>
                              <td className="px-4 py-1.5">{p.groups ? <span className="text-muted-foreground mr-1">{open ? '▾' : '▸'}</span> : null}{p.page_name || '—'}</td>
                              <td className="px-4 py-1.5 text-right text-sky-400">{p.groups || ''}</td>
                              <td className="px-4 py-1.5 text-right text-fuchsia-400">{p.stories || ''}</td>
                              <td className="px-4 py-1.5 text-right font-semibold text-emerald-400">{p.invites ? p.invites.toLocaleString('pt-BR') : ''}</td>
                              <td className="px-4 py-1.5 text-muted-foreground whitespace-nowrap">{fmtTime(p.last_at)}</td>
                            </tr>
                            {open && (
                              <tr className="bg-muted/10"><td colSpan={5} className="px-4 py-2">
                                <div className="text-[11px] text-muted-foreground mb-1">Posts em grupo desta página ({gposts.length}):</div>
                                <div className="space-y-0.5">
                                  {gposts.map(g => (
                                    <div key={g.id} className="flex items-center gap-2 text-[11px]">
                                      <span className="text-muted-foreground whitespace-nowrap">{fmtTime(g.posted_at)}</span>
                                      {g.target_url ? <a href={g.target_url} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline truncate max-w-[240px]">{g.target_name || 'grupo'}</a> : <span className="truncate max-w-[240px]">{g.target_name || 'grupo'}</span>}
                                      {g.post_url ? <a href={g.post_url} target="_blank" rel="noreferrer" className="text-primary hover:underline ml-auto">ver post ↗</a> : <span className="ml-auto text-red-400">{g.status || 'falhou'}</span>}
                                    </div>
                                  ))}
                                  {gposts.length === 0 && <div className="text-[11px] text-muted-foreground">(sem detalhe dos posts — o log pode ter passado do limite do dia)</div>}
                                </div>
                              </td></tr>
                            )}
                          </Fragment>
                        )
                      })}
                      {(!repData || repData.pages.length === 0) && <tr><td colSpan={5} className="px-4 py-3 text-muted-foreground">Nenhuma atividade nesse dia.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Convites p/ grupos — por grupo (quantos cada grupo recebeu das páginas) */}
              {(repData?.group_invites_by_group?.length ?? 0) > 0 && (
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="px-4 py-2 text-xs text-muted-foreground border-b border-border/60">Convites p/ grupos — por grupo</div>
                  <div className="max-h-[420px] overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
                        <th className="px-4 py-2">Grupo</th><th className="px-4 py-2 text-right">Convites recebidos</th><th className="px-4 py-2 text-right">Páginas</th>
                      </tr></thead>
                      <tbody>
                        {(repData?.group_invites_by_group || []).map((g, i) => (
                          <tr key={i} className="border-t border-border/40 hover:bg-muted/20">
                            <td className="px-4 py-1.5">{g.group_url ? <a href={g.group_url} target="_blank" rel="noreferrer" className="text-fuchsia-400 hover:underline">{g.group_name}</a> : g.group_name}</td>
                            <td className="px-4 py-1.5 text-right font-semibold text-fuchsia-300">{g.invites.toLocaleString('pt-BR')}</td>
                            <td className="px-4 py-1.5 text-right text-muted-foreground">{g.pages}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'esteira' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-xs text-muted-foreground">
                  Meta: <span className="text-foreground font-semibold">{(growthData?.meta.target ?? 1000).toLocaleString('pt-BR')} seguidores em {growthData?.meta.days ?? 5} dias</span> · taxa medida na janela de {growthData?.meta.window ?? 7}d{growthData?.scoped ? ' · só páginas no creator' : ''}
                </div>
                <span className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> ao vivo (60s)
                  <button onClick={() => growthRefetch()} className="ml-1 rounded-md border border-border px-2 py-0.5 hover:bg-muted/40">{growthFetching ? '…' : 'atualizar'}</button>
                </span>
              </div>

              {/* Termômetro da esteira */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {([
                  ['Atingiram a meta', growthData?.resumo?.atingido ?? 0, '#22c55e'],
                  ['No ritmo', growthData?.resumo?.no_ritmo ?? 0, '#38bdf8'],
                  ['Atrasadas', growthData?.resumo?.atrasado ?? 0, '#f59e0b'],
                  ['Paradas', growthData?.resumo?.parado ?? 0, '#ef4444'],
                  ['Sem dados', growthData?.resumo?.sem_dados ?? 0, '#a3a3a3'],
                  ['Taxa mediana/dia', growthData?.resumo?.taxa_mediana ?? 0, '#c084fc'],
                ] as const).map(([label, val, color]) => (
                  <div key={label} className="rounded-xl border border-border bg-card p-4">
                    <div className="text-2xl font-bold" style={{ color }}>{val.toLocaleString('pt-BR')}</div>
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground -mt-1">
                Ritmo necessário para a meta: <span className="text-foreground">{(growthData?.resumo?.ritmo_necessario ?? 0).toLocaleString('pt-BR')} seguidores/dia</span>. As páginas que exigem atenção (atrasadas e paradas) vêm primeiro.
              </div>

              {/* Tabela por página */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="max-h-[560px] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
                      <th className="px-4 py-2">Página</th><th className="px-4 py-2 text-right">Seguidores</th><th className="px-4 py-2 text-right">Taxa/dia</th><th className="px-4 py-2 text-right">Falta p/ meta</th><th className="px-4 py-2 text-right">Idade</th><th className="px-4 py-2">Situação</th>
                    </tr></thead>
                    <tbody>
                      {(growthData?.pages || []).map(p => (
                        <tr key={p.page_id} className="border-t border-border/40 hover:bg-muted/20">
                          <td className="px-4 py-1.5">{p.name || '—'}</td>
                          <td className="px-4 py-1.5 text-right font-semibold">{p.followers.toLocaleString('pt-BR')}</td>
                          <td className="px-4 py-1.5 text-right" style={{ color: p.rate_per_day == null ? undefined : p.rate_per_day > 0 ? '#22c55e' : '#ef4444' }}>{p.rate_per_day == null ? '—' : `${p.rate_per_day > 0 ? '+' : ''}${p.rate_per_day}`}</td>
                          <td className="px-4 py-1.5 text-right text-muted-foreground">{p.status === 'atingido' ? '✓' : p.eta_days == null ? '—' : `~${p.eta_days.toLocaleString('pt-BR')}d`}</td>
                          <td className="px-4 py-1.5 text-right text-muted-foreground">{p.age_days == null ? '—' : `${p.age_days.toLocaleString('pt-BR')}d`}</td>
                          <td className="px-4 py-1.5"><GrowthPill status={p.status} /></td>
                        </tr>
                      ))}
                      {(!growthData || growthData.pages.length === 0) && <tr><td colSpan={6} className="px-4 py-3 text-muted-foreground">{growthFetching ? 'Carregando…' : 'Sem páginas para estimar ainda.'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === 'catalogo' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <input value={catQ} onChange={e => setCatQ(e.target.value)} placeholder="filtrar por nome ou doc_id…" className="rounded-md border border-border bg-background px-2 py-1 text-xs w-64" />
                <span className="text-xs text-muted-foreground">{catalogData?.count ?? 0} doc_ids no catálogo global (blissf)</span>
                <button onClick={() => catalogRefetch()} className="ml-auto rounded-md border border-border px-2 py-0.5 text-xs hover:bg-muted/40">{catalogFetching ? '…' : 'atualizar'}</button>
              </div>
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="max-h-[560px] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
                      <th className="px-4 py-2">Query (friendly name)</th><th className="px-4 py-2">doc_id</th><th className="px-4 py-2">Visto por último</th>
                    </tr></thead>
                    <tbody>
                      {(catalogData?.entries || [])
                        .filter(e => !catQ || (e.friendly || '').toLowerCase().includes(catQ.toLowerCase()) || (e.doc_id || '').includes(catQ))
                        .sort((a, b) => (a.friendly || '').localeCompare(b.friendly || ''))
                        .map((e, i) => (
                          <tr key={i} className="border-t border-border/40 hover:bg-muted/20">
                            <td className="px-4 py-1.5">{e.friendly}</td>
                            <td className="px-4 py-1.5 font-mono text-muted-foreground">{e.doc_id}</td>
                            <td className="px-4 py-1.5 text-muted-foreground whitespace-nowrap">{e.last_seen ? new Date(e.last_seen).toLocaleString('pt-BR') : '—'}</td>
                          </tr>
                        ))}
                      {(!catalogData || catalogData.entries.length === 0) && <tr><td colSpan={3} className="px-4 py-3 text-muted-foreground">{catalogFetching ? 'Carregando…' : 'Catálogo vazio.'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
